import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { OmgevingsData } from '../../types';

// Color maps per data type
const MELDING_COLORS: Record<string, string> = {
  open: '#ef4444',
  in_behandeling: '#f59e0b',
  afgehandeld: '#22c55e',
};

const WERKZAAMHEID_COLORS: Record<string, string> = {
  SPIN: '#f97316',
  MELVIN: '#8b5cf6',
};

const VERGUNNING_COLORS: Record<string, string> = {
  omgevingsvergunning: '#10b981',
  verkeersbesluit: '#f59e0b',
  evenement: '#ec4899',
  horeca: '#f97316',
  kapvergunning: '#22c55e',
  overig: '#6b7280',
};

function createColoredIcon(color: string) {
  return L.divIcon({
    className: 'omgevingsdata-marker',
    html: `<svg width="28" height="28" viewBox="0 0 24 24" fill="${color}" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
    </svg>`,
    iconSize: [28, 28],
    iconAnchor: [14, 28],
    popupAnchor: [0, -28],
  });
}

interface OmgevingsDataLayerProps {
  data: OmgevingsData | null;
  activeFilters: Record<string, boolean>;
  onMarkerClick?: (lat: number, lng: number) => void;
}

export const OmgevingsDataLayer: React.FC<OmgevingsDataLayerProps> = ({
  data,
  activeFilters,
  onMarkerClick,
}) => {
  const map = useMap();
  const markersRef = useRef<L.Marker[]>([]);

  useEffect(() => {
    // Clear existing markers
    markersRef.current.forEach(m => map.removeLayer(m));
    markersRef.current = [];

    if (!data) return;

    // Meldingen
    if (activeFilters.meldingen) {
      const bronColors: Record<string, string> = { automatisch: '#277CEA', handmatig: '#9DA6A8' };
      const typeLabels: Record<string, string> = { bewoner: 'Bewoner', media: 'Media', gemeente: 'Gemeente', monitoring: 'Monitoring' };
      const typeColors: Record<string, string> = { bewoner: '#8b5cf6', media: '#ec4899', gemeente: '#0ea5e9', monitoring: '#f59e0b' };

      data.meldingen.forEach(melding => {
        if (!melding.lat || !melding.lng) return;
        const color = MELDING_COLORS[melding.status] || '#ef4444';
        const statusLabel = melding.status === 'open' ? 'Open' :
          melding.status === 'in_behandeling' ? 'In behandeling' : 'Afgehandeld';
        const bronLabel = melding.bron === 'automatisch' ? 'Automatisch (API)' : melding.bron === 'handmatig' ? 'Handmatig' : '';
        const bronClr = bronColors[melding.bron || ''] || '#9DA6A8';
        const typeLabel = typeLabels[melding.meldingType || ''] || '';
        const typeClr = typeColors[melding.meldingType || ''] || '#9DA6A8';
        const datum = new Date(melding.createdAt).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });
        const locStr = `${melding.lat.toFixed(5)}, ${melding.lng.toFixed(5)}`;

        const marker = L.marker([melding.lat, melding.lng], { icon: createColoredIcon(color) })
          .bindPopup(`
            <div style="font-family:'Open Sans',sans-serif;min-width:200px;">
              <strong style="font-size:13px;color:#323232;">Melding</strong>
              <span style="display:inline-block;margin-left:6px;padding:1px 6px;font-size:10px;color:white;background:${color};border-radius:10px;">${statusLabel}</span>
              <p style="font-size:12px;color:#666;margin:6px 0 4px;">${melding.text}</p>
              <span style="font-size:11px;color:#9DA6A8;">${melding.category}${melding.subcategory ? ' — ' + melding.subcategory : ''}</span>
              <div style="margin-top:8px;padding-top:6px;border-top:1px solid #f0f0f0;display:flex;flex-direction:column;gap:3px;">
                <div style="display:flex;align-items:center;gap:4px;">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9DA6A8" stroke-width="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
                  <span style="font-size:10px;color:${bronClr};font-weight:600;">${bronLabel}</span>
                  ${melding.bronDetail ? `<span style="font-size:10px;color:#bbb;">— ${melding.bronDetail}</span>` : ''}
                </div>
                ${typeLabel ? `<div style="display:flex;align-items:center;gap:4px;">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9DA6A8" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                  <span style="font-size:10px;color:${typeClr};font-weight:600;">${typeLabel}</span>
                </div>` : ''}
                <div style="display:flex;align-items:center;gap:4px;">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9DA6A8" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                  <span style="font-size:10px;color:#666;">${datum}</span>
                </div>
                <div style="display:flex;align-items:center;gap:4px;">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9DA6A8" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                  <span style="font-size:10px;color:#bbb;">${locStr}</span>
                </div>
              </div>
            </div>
          `)
          .addTo(map);
        if (onMarkerClick) {
          marker.on('click', () => onMarkerClick(melding.lat, melding.lng));
        }
        markersRef.current.push(marker);
      });
    }

    // Werkzaamheden
    if (activeFilters.werkzaamheden) {
      const catColors: Record<string, string> = { project: '#1E3A2F', raakvlak: '#f97316' };
      const catLabels: Record<string, string> = { project: 'Projectwerkzaamheden', raakvlak: 'Raakvlakproject' };
      const hinderLabels: Record<string, string> = {
        geluid_nacht: 'Geluidsoverlast (nacht)', wegafsluiting: 'Wegafsluiting', omleiding: 'Omleiding',
        trillingen: 'Trillingen', stof_overlast: 'Stof/sloopoverlast', verkeer: 'Verkeershinder',
      };

      data.werkzaamheden.forEach(werk => {
        if (!werk.lat || !werk.lng) return;
        const isProject = werk.werkCategorie === 'project';
        const color = catColors[werk.werkCategorie || 'raakvlak'] || '#f97316';
        const catLabel = catLabels[werk.werkCategorie || 'raakvlak'] || 'Raakvlakproject';
        const hinderLabel = werk.hinderType ? hinderLabels[werk.hinderType] || werk.hinderType : '';
        const dateRange = werk.startDate && werk.endDate
          ? `${new Date(werk.startDate).toLocaleDateString('nl-NL')} — ${new Date(werk.endDate).toLocaleDateString('nl-NL')}`
          : '';

        // Ander icoon voor project vs raakvlak
        const icon = isProject ? createColoredIcon(color) : L.divIcon({
          className: 'omgevingsdata-marker',
          html: `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="${color}" fill-opacity="0.3" stroke="${color}" stroke-width="1.5"/>
            <circle cx="12" cy="9" r="3" fill="${color}"/>
          </svg>`,
          iconSize: [28, 28],
          iconAnchor: [14, 28],
          popupAnchor: [0, -28],
        });

        const werkUrl = werk.url || null;

        const marker = L.marker([werk.lat, werk.lng], { icon })
          .bindPopup(`
            <div style="font-family:'Open Sans',sans-serif;min-width:200px;">
              <strong style="font-size:13px;color:#323232;">Werkzaamheid</strong>
              <span style="display:inline-block;margin-left:6px;padding:1px 6px;font-size:10px;color:white;background:${color};border-radius:10px;">${catLabel}</span>
              <p style="font-size:12px;color:#666;margin:6px 0 4px;">${werk.description}</p>
              <div style="display:flex;flex-wrap:wrap;gap:4px;margin-top:4px;">
                <span style="font-size:10px;padding:1px 6px;border:1px solid ${WERKZAAMHEID_COLORS[werk.source] || '#f97316'};color:${WERKZAAMHEID_COLORS[werk.source] || '#f97316'};border-radius:10px;">${werk.source}</span>
                ${hinderLabel ? `<span style="font-size:10px;padding:1px 6px;border:1px solid #ef4444;color:#ef4444;border-radius:10px;">${hinderLabel}</span>` : ''}
              </div>
              ${dateRange ? `<div style="margin-top:6px;"><span style="font-size:10px;color:#bbb;">${dateRange}</span></div>` : ''}
              ${werkUrl ? `<div style="margin-top:6px;"><a href="${werkUrl}" target="_blank" rel="noopener" style="font-size:11px;color:#1E3A2F;">Bekijk op MELVIN</a></div>` : ''}
            </div>
          `)
          .addTo(map);
        if (onMarkerClick) {
          marker.on('click', () => onMarkerClick(werk.lat, werk.lng));
        }
        markersRef.current.push(marker);
      });
    }


    // Vergunningen (only those with coordinates)
    if (activeFilters.vergunningen) {
      data.vergunningen.forEach(verg => {
        if (!verg.lat || !verg.lng) return;
        const color = VERGUNNING_COLORS[verg.type] || '#6b7280';
        const marker = L.marker([verg.lat, verg.lng], { icon: createColoredIcon(color) })
          .bindPopup(`
            <div style="font-family:'Open Sans',sans-serif;min-width:180px;">
              <strong style="font-size:13px;color:#323232;">Vergunning</strong>
              <span style="display:inline-block;margin-left:6px;padding:1px 6px;font-size:10px;color:white;background:${color};border-radius:10px;">${verg.type}</span>
              <p style="font-size:12px;color:#666;margin:6px 0 4px;">${verg.title}</p>
              ${verg.date ? `<span style="font-size:10px;color:#bbb;">${new Date(verg.date).toLocaleDateString('nl-NL')}</span>` : ''}
              ${verg.url ? `<br/><a href="${verg.url}" target="_blank" rel="noopener" style="font-size:11px;color:#1E3A2F;">Bekijk op overheid.nl</a>` : ''}
            </div>
          `)
          .addTo(map);
        if (onMarkerClick) {
          marker.on('click', () => onMarkerClick(verg.lat, verg.lng));
        }
        markersRef.current.push(marker);
      });
    }

    return () => {
      markersRef.current.forEach(m => map.removeLayer(m));
      markersRef.current = [];
    };
  }, [data, activeFilters, map, onMarkerClick]);

  return null;
};
